import React, { useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import {
  Autocomplete,
  Button,
  Card,
  CardHeader,
  Collapse,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
} from '@mui/material'
import ErrorFallback from '~/components/ErrorFallback'
import Layout from '~/components/Layout'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FilterAutocompleteAgencies from '~/components/FilterAutocompleteAgencies'
import useReportState from '~/features/Report/stores/ReportState'
import { downloadReportCSV } from '~/features/Report/services/report'
import ExportButtonCSV from '~/features/Report/components/ButtonExportCSV'
import GenerateReportButton from '~/features/Report/components/GenerateReportButton'
import { makeRequestTogetReportAllUSers } from './services'
import FilterAutocompletePermissionGroup from '~/components/FilterAutocompletePermissionGroup'
import notify from '~/utils/notify'
import { generateCSVFileName } from '~/utils/strings'

// eslint-disable-next-line max-lines-per-function
export default function AllUsers() {
  const { translate } = useLang()

  const expandFilterSearch = useHookstate<boolean>(true)
  const isLoading = useHookstate<boolean>(false)
  const lockFields = useHookstate<boolean>(false)
  const agencyIds = useHookstate<readonly number[]>([])
  const groupId = useHookstate<number | null>(null)
  const resetValeus = useHookstate(false)
  const labelButton = useHookstate('')
  const options = ['Day', 'Week', 'Month', 'Year']
  const activeInLast = useHookstate<number | null>(null)
  const period = useHookstate<string | null>(null)
  const value = useHookstate<number | null>(null)

  const { reportData, loadingReport } = useReportState()

  useEffect(() => {
    document.title = translate('Reports - Dispatch System')
  }, [])

  const convertPeriodInDays = () => {
    const activeInLastValue = activeInLast.get()

    if (activeInLastValue == null) {
      return
    }

    switch (period.get()) {
      case 'Week':
        activeInLast.set(activeInLastValue * 7)
        break
      case 'Month':
        activeInLast.set(activeInLastValue * 30)
        break
      case 'Year':
        activeInLast.set(activeInLastValue * 365)
    }
  }

  const handleNumberOfDays = (numberOfDay: number) => {
    if (numberOfDay < 1) {
      value.set(1)
      activeInLast.set(1)
    } else {
      value.set(numberOfDay)
      activeInLast.set(numberOfDay)
    }
  }

  async function generateReport(exportCSV = false) {
    convertPeriodInDays()
    loadingReport().set(true)
    isLoading.set(true)
    const data = await makeRequestTogetReportAllUSers({
      agencyIds: agencyIds.get(),
      activeInLast: activeInLast.get(),
      groupId: groupId.get(),
      exportCSV: exportCSV,
    }).finally(() => {
      loadingReport().set(false)
      isLoading.set(false)
    })

    if (!data || (Array.isArray(data) && data.length === 0)) {
      notify({
        message: translate('Não há dados suficientes para gerar o relatório'),
        type: 'error',
      })
      return
    }

    if (exportCSV) {
      downloadReportCSV(generateCSVFileName('List of Users', translate), data)
    } else {
      setTimeout(() => {
        reportData().set(data)
      })
      window.open(`/report/result/list-of-users`)
    }
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Layout marginLayout={true}>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <Grid container xs={12}>
            <Grid item>
              <CardHeader
                title={`${translate('Report')}: ${translate(`List of Users`)}`}
              />
            </Grid>
          </Grid>
        </Card>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <CardHeader
            title={`${translate('Filters')}`}
            avatar={
              <IconButton
                onClick={() =>
                  expandFilterSearch.set(!expandFilterSearch.get())
                }
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  marginRight: 1,
                  padding: 0,
                }}
              >
                {expandFilterSearch.value ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
            }
          />
          <Collapse in={expandFilterSearch.get()}>
            <Grid
              container
              xs={12}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: 2,
              }}
            >
              <Grid container direction={'row'} rowGap={2}>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteAgencies
                    disabled={lockFields.get()}
                    agenciesId={agencyIds.get()}
                    onAgenciesChange={(agency) => {
                      labelButton.set(translate('Search'))
                      agencyIds.set(agency)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompletePermissionGroup
                    groupId={groupId.get() ?? undefined}
                    onGroupChange={(disptachGroupValue) => {
                      groupId.set(disptachGroupValue)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <Autocomplete
                    options={options}
                    sx={{ height: 2 }}
                    size="small"
                    value={period.get()}
                    getOptionLabel={(option) => translate(option)}
                    renderInput={(params) => (
                      <TextField {...params} label={translate('Period')} />
                    )}
                    onChange={(_, valuePeriod) => {
                      if (!valuePeriod) {
                        period.set(null)
                        value.set(null)
                        activeInLast.set(null)
                      } else {
                        period.set(valuePeriod)
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={value.get() ? value.get() : ''}
                    disabled={!period.get()}
                    onChange={(event) => {
                      handleNumberOfDays(Number(event.target.value))
                    }}
                    InputProps={{
                      inputProps: { min: 1 },
                    }}
                  />
                  <FormHelperText id="component-helper-text">
                    {translate('Active in the last (days)')}
                  </FormHelperText>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                  pt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    agencyIds.set([])
                    period.set(null)
                    activeInLast.set(null)
                    groupId.set(null)
                    value.set(null)
                  }}
                  sx={{ height: '40px' }}
                >
                  {translate('Clear Filters')}
                </Button>
                <ExportButtonCSV
                  isLoading={isLoading.get()}
                  dataToExport={() => generateReport(true)}
                />
                <GenerateReportButton
                  isLoading={isLoading.get()}
                  makeReport={() => generateReport()}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Layout>
    </ErrorBoundary>
  )
}
